/**
 * DealSelector - Seletor de negócio (deal) com busca via RPC
 * 
 * Componente reutilizável para vincular tarefas a negócios do CRM.
 * Suporta filtro por contato e callback com dados do deal selecionado.
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-07 v2.0.0 - Migração para RPC + props contactId e onDealData
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Briefcase } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { formatDealCode } from '@/lib/formatters';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/** Interface do negócio para listagem (inclui contact_id para auto-preenchimento) */
interface DealOption {
  id: string;
  title: string;
  value: number | null;
  contact_id: string | null;
}

interface DealSelectorProps {
  value?: string | null;
  onSelect: (dealId: string | null) => void;
  /** Filtra negócios pelo contato vinculado */
  contactId?: string | null;
  /** Callback com dados completos do deal selecionado (para auto-preencher contato) */
  onDealData?: (deal: { id: string; contact_id: string | null } | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Seletor de negócio com busca por título via RPC server-side
 */
export function DealSelector({
  value,
  onSelect,
  contactId,
  onDealData,
  placeholder = 'Selecionar negócio...',
  disabled = false,
  className,
}: DealSelectorProps) {
  const { currentTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Busca negócios via RPC com filtro opcional por contato
  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['deals-selector', currentTenant?.id, search, contactId],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      const { data, error } = await supabase.rpc('search_deals_for_selector', {
        p_search: search || null,
        p_contact_id: contactId || null,
        p_tenant_hint: currentTenant.id,
      });
      if (error) throw error;
      return (data || []) as DealOption[];
    },
    enabled: !!currentTenant?.id && open,
  });

  // Busca negócio selecionado via RPC (para exibição)
  const { data: selectedDeal } = useQuery({
    queryKey: ['deal-selected', value],
    queryFn: async () => {
      if (!value) return null;
      const { data, error } = await supabase.rpc('search_deals_for_selector', {
        p_deal_id: value,
      });
      if (error || !data || data.length === 0) return null;
      return data[0] as DealOption;
    },
    enabled: !!value,
  });

  /** Handler de seleção — chama onSelect e onDealData */
  const handleSelect = (deal: DealOption | null) => {
    if (deal) {
      onSelect(deal.id);
      onDealData?.({ id: deal.id, contact_id: deal.contact_id });
    } else {
      onSelect(null);
      onDealData?.(null);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex items-center gap-2 truncate">
            <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className={cn('truncate', !selectedDeal && 'text-muted-foreground')}>
              {selectedDeal
                ? `${formatDealCode(selectedDeal.id)} — ${selectedDeal.title}`
                : placeholder}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar negócio..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Buscando...' : 'Nenhum negócio encontrado.'}
            </CommandEmpty>
            <CommandGroup>
              {/* Opção para limpar seleção */}
              {value && (
                <CommandItem
                  value="clear"
                  onSelect={() => handleSelect(null)}
                  className="text-muted-foreground"
                >
                  <Briefcase className="mr-2 h-4 w-4" />
                  Nenhum negócio
                </CommandItem>
              )}
              {deals.map((deal) => (
                <CommandItem
                  key={deal.id}
                  value={deal.id}
                  onSelect={() => handleSelect(deal)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-mono text-xs text-muted-foreground shrink-0">{formatDealCode(deal.id)}</span>
                    <span className="truncate font-medium">{deal.title}</span>
                  </div>
                  <Check
                    className={cn('ml-2 h-4 w-4', value === deal.id ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
